<template>
  <div class="billing-dashboard">
    <EntraPageHeader
      title="Microsoft Billing Dashboard"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="false"
    />

    <div class="page-body">
      <div v-if="error" class="error-message">
        <span>{{ error }}</span>
        <button class="retry-btn" @click="loadData">Retry</button>
      </div>

      <div v-else-if="dashboardState === 'loading'" class="loading-state" aria-busy="true" aria-label="Loading billing data">
        <div class="skeleton-row">
          <div class="skeleton skeleton-card"></div>
          <div class="skeleton skeleton-card"></div>
          <div class="skeleton skeleton-card"></div>
        </div>
        <div class="skeleton skeleton-chart"></div>
      </div>

      <template v-else>
        <div class="dashboard-header">
          <div class="dashboard-header-left">
            <div class="range-tabs">
              <button
                v-for="range in timeRanges"
                :key="range.value"
                class="range-tab"
                :class="{ active: selectedRange === range.value }"
                @click="selectRange(range.value)"
              >{{ range.label }}</button>
            </div>
            <div class="currency-filter">
              <label class="currency-filter-label" for="billing-region-filter">Region:</label>
              <select id="billing-region-filter" :value="selectedRegion" class="currency-filter-select" @change="selectRegion($event.target.value)">
                <option value="ALL">All regions</option>
                <option v-for="r in availableRegions" :key="r" :value="r">{{ r }}</option>
              </select>
            </div>
            <div class="currency-filter">
              <label class="currency-filter-label" for="billing-currency-filter">Currency:</label>
              <select id="billing-currency-filter" v-model="selectedCurrency" class="currency-filter-select">
                <option value="ALL">All currencies</option>
                <option v-for="c in availableCurrencies" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div class="dashboard-header-right">
            <span class="sync-meta">Last sync: {{ lastSyncLabel }}</span>
            <button class="sync-btn" :class="{ syncing: syncing }" :disabled="syncing" @click="triggerSync">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {{ syncing ? 'Syncing...' : 'Pull Fresh Data' }}
            </button>
            <span v-if="syncStatus" class="sync-status" :class="'sync-status-' + syncStatusType">{{ syncStatus }}</span>
          </div>
        </div>

        <div class="kpi-grid">
          <div class="kpi-hero metric-card">
            <div class="metric-label">Total Spend{{ isAllCurrencies ? ' (per currency)' : ' (' + selectedCurrency + ')' }}</div>
            <template v-if="isAllCurrencies">
              <div v-if="perCurrencyTotals.length > 0" class="hero-currency-row">
                <div v-for="t in perCurrencyTotals" :key="t.currency" class="hero-currency-block">
                  <div class="hero-currency-code">{{ t.currency }}</div>
                  <div class="metric-value" :title="formatCurrency(t.total, t.currency)">{{ formatCompactCurrency(t.total, t.currency) }}</div>
                  <div class="metric-sub">{{ formatCurrency(t.total, t.currency) }}</div>
                </div>
              </div>
              <div v-else class="metric-value">--</div>
            </template>
            <template v-else>
              <div class="metric-value" :title="formatCurrency(totalSpend, selectedCurrency)">{{ formatCompactCurrency(totalSpend, selectedCurrency) }}</div>
              <div class="metric-sub">{{ formatCurrency(totalSpend, selectedCurrency) }}</div>
            </template>
          </div>
          <MetricCard label="Invoices" :value="invoiceCount" :state="dashboardState" />
          <MetricCard label="Regions Synced" :value="regionsCount" :state="dashboardState" />
          <MetricCard label="Licence Seats" :value="totalSeatsLabel" :state="dashboardState" />
          <MetricCard label="Top Customer" :value="topCustomerName" :state="dashboardState" />
          <MetricCard label="Top Product" :value="topProductName" :state="dashboardState" />
        </div>

        <div class="dashboard-grid">
          <div class="card chart-card-wide">
            <h3>Monthly Spend Trend{{ isAllCurrencies ? ' (per currency)' : '' }}</h3>
            <template v-if="isAllCurrencies">
              <div v-if="trendByCurrency.length > 0" class="trend-multi">
                <div v-for="t in trendByCurrency" :key="t.currency" class="trend-mini">
                  <h4 class="mini-chart-title">{{ t.currency }}</h4>
                  <div class="chart-wrapper-mini">
                    <BillingBarChart
                      :labels="t.labels"
                      :values="t.values"
                      label="Total Spend"
                      :currency="t.currency + ' '"
                      :auto-skip="true"
                      :max-ticks-limit="42"
                    />
                  </div>
                </div>
              </div>
              <p v-else class="no-data">No monthly data available</p>
            </template>
            <template v-else>
              <div v-if="monthlyChartLabels.length > 0" class="chart-wrapper">
                <BillingBarChart
                  :labels="monthlyChartLabels"
                  :values="monthlyChartValues"
                  label="Total Spend"
                  :currency="selectedCurrency + ' '"
                  :auto-skip="true"
                  :max-ticks-limit="42"
                />
              </div>
              <p v-else class="no-data">No monthly data available</p>
            </template>
          </div>

          <div class="card">
            <h3>Spend by Region ({{ isAllCurrencies ? 'per currency' : selectedCurrency }})</h3>
            <template v-if="isAllCurrencies">
              <div v-if="doughnutsByCurrency.length > 0" class="doughnut-multi">
                <div v-for="d in doughnutsByCurrency" :key="d.currency" class="doughnut-mini">
                  <h4 class="mini-chart-title">{{ d.currency }}</h4>
                  <div class="chart-wrapper-doughnut">
                    <BillingDoughnutChart
                      :labels="d.labels"
                      :values="d.values"
                      :colors="d.colors"
                      :currency="d.currency + ' '"
                    />
                  </div>
                </div>
              </div>
              <p v-else class="no-data">No spend data available</p>
            </template>
            <template v-else>
              <div v-if="filteredRegionRows.length > 0" class="chart-wrapper-doughnut">
                <BillingDoughnutChart
                  :labels="regionChartLabels"
                  :values="regionChartValues"
                  :colors="regionChartColors"
                  :currency="selectedCurrency + ' '"
                  :center-value="String(regionChartLabels.length)"
                  center-label="Regions"
                />
              </div>
              <p v-else class="no-data">No spend data available in {{ selectedCurrency }}</p>
            </template>
          </div>

          <div class="tables-column">
            <div class="card">
              <h3>Top Customers ({{ isAllCurrencies ? 'per currency' : selectedCurrency }})</h3>
              <template v-if="isAllCurrencies">
                <div v-if="topCustomersGrouped.length > 0">
                  <div v-for="g in topCustomersGrouped" :key="g.currency" class="currency-group">
                    <h4 class="currency-group-title">{{ g.currency }}</h4>
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Region</th>
                          <th>Spend</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(customer, idx) in g.rows" :key="idx" :style="{ borderLeftColor: getRegionColor(customer.region_code) }">
                          <td>{{ customer.customer_name || 'Unknown' }}</td>
                          <td><span class="region-badge">{{ customer.region_code }}</span></td>
                          <td class="currency-cell">{{ formatCurrency(customer.total_spend, customer.currency) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <p v-else class="no-data">No customer data available</p>
              </template>
              <template v-else>
                <div v-if="topCustomersFiltered.length > 0" class="trend-table">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Region</th>
                        <th>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(customer, idx) in topCustomersFiltered" :key="idx" :style="{ borderLeftColor: getRegionColor(customer.region_code) }">
                        <td>{{ customer.customer_name || 'Unknown' }}</td>
                        <td><span class="region-badge">{{ customer.region_code }}</span></td>
                        <td class="currency-cell">{{ formatCurrency(customer.total_spend, customer.currency) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="no-data">No customer data available in {{ selectedCurrency }}</p>
              </template>
            </div>

            <div class="card">
              <h3>Top Products ({{ isAllCurrencies ? 'per currency' : selectedCurrency }})</h3>
              <template v-if="isAllCurrencies">
                <div v-if="topProductsGrouped.length > 0">
                  <div v-for="g in topProductsGrouped" :key="g.currency" class="currency-group">
                    <h4 class="currency-group-title">{{ g.currency }}</h4>
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Region</th>
                          <th>Spend</th>
                          <th>Seats / Usage</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(product, idx) in g.rows" :key="idx" :style="{ borderLeftColor: getRegionColor(product.region_code) }">
                          <td>{{ product.product_name || 'Unknown' }}</td>
                          <td><span class="region-badge">{{ product.region_code }}</span></td>
                          <td class="currency-cell">{{ formatCurrency(product.total_spend, product.currency) }}</td>
                          <td>{{ formatProductQuantity(product) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <p v-else class="no-data">No product data available</p>
              </template>
              <template v-else>
                <div v-if="topProductsFiltered.length > 0" class="trend-table">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Region</th>
                        <th>Spend</th>
                        <th>Seats / Usage</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, idx) in topProductsFiltered" :key="idx" :style="{ borderLeftColor: getRegionColor(product.region_code) }">
                        <td>{{ product.product_name || 'Unknown' }}</td>
                        <td><span class="region-badge">{{ product.region_code }}</span></td>
                        <td class="currency-cell">{{ formatCurrency(product.total_spend, product.currency) }}</td>
                        <td>{{ formatProductQuantity(product) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="no-data">No product data available in {{ selectedCurrency }}</p>
              </template>
            </div>
          </div>
        </div>

        <div class="card sync-state-card">
          <h3>Sync Status by Region</h3>
          <div v-if="syncStateRows.length > 0" class="trend-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Job Type</th>
                  <th>Last Synced</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(state, idx) in syncStateRows" :key="idx" :style="{ borderLeftColor: getRegionColor(state.region_code) }">
                  <td><span class="region-badge">{{ state.region_code }}</span></td>
                  <td>{{ state.job_type }}</td>
                  <td>{{ formatRelativeTime(state.last_synced_at) }}</td>
                  <td>
                    <span class="status-chip" :class="'status-' + state.last_status">{{ state.last_status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No sync history available</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import BillingBarChart from '@/components/billing/BillingBarChart.vue';
import BillingDoughnutChart from '@/components/billing/BillingDoughnutChart.vue';
import { getBillingDashboard, getBillingRegions, triggerBillingSync, getBillingJobStatus } from '@/services/billing-api.js';
import { formatCurrency, formatCount, formatCompactCurrency, getRegionColor, REGION_COLORS } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';

export default {
  name: 'BillingDashboard',
  components: { EntraPageHeader, MetricCard, BillingBarChart, BillingDoughnutChart },
  data: function() {
    return {
      dashboard: {},
      dashboardState: 'loading',
      error: '',
      syncing: false,
      syncStatus: '',
      syncStatusType: '',
      pollTimer: null,
      loadToken: 0,
      selectedCurrency: 'GBP',
      selectedRegion: 'ALL',
      regionOptions: [],
      selectedRange: 12,
      timeRanges: [
        { label: '1 Month', value: 1 },
        { label: '3 Months', value: 3 },
        { label: '6 Months', value: 6 },
        { label: '1 Year', value: 12 },
        { label: '2 Years', value: 24 },
        { label: 'All', value: 60 }
      ]
    };
  },
  computed: {
    isAllCurrencies: function() {
      return this.selectedCurrency === 'ALL';
    },
    availableCurrencies: function() {
      var seen = {};
      var add = function(c) { if (c) seen[c] = true; };
      (this.dashboard.totalSpendByRegion || []).forEach(function(r) { add(r.currency_code); });
      (this.dashboard.monthlySpend || []).forEach(function(r) { add(r.currency_code); });
      (this.dashboard.topCustomers || []).forEach(function(r) { add(r.currency); });
      (this.dashboard.topProducts || []).forEach(function(r) { add(r.currency); });
      var list = Object.keys(seen).sort();
      if (list.length === 0 && !this.isAllCurrencies) list = [this.selectedCurrency];
      return list;
    },
    availableRegions: function() {
      if (this.regionOptions.length > 0) return this.regionOptions;
      var seen = {};
      (this.dashboard.totalSpendByRegion || []).forEach(function(r) { if (r.region_code) seen[r.region_code] = true; });
      (this.dashboard.syncState || []).forEach(function(r) { if (r.region_code) seen[r.region_code] = true; });
      return Object.keys(seen).sort();
    },
    filteredRegionRows: function() {
      var self = this;
      return (this.dashboard.totalSpendByRegion || []).filter(function(r) {
        if (!self.inSelectedRegion(r)) return false;
        return self.isAllCurrencies ? true : (r.currency_code || '') === self.selectedCurrency;
      });
    },
    topCustomersFiltered: function() {
      var self = this;
      return (this.dashboard.topCustomers || []).filter(function(r) {
        if (!self.inSelectedRegion(r)) return false;
        return self.isAllCurrencies ? true : (r.currency || '') === self.selectedCurrency;
      });
    },
    topProductsFiltered: function() {
      var self = this;
      return (this.dashboard.topProducts || []).filter(function(r) {
        if (!self.inSelectedRegion(r)) return false;
        return self.isAllCurrencies ? true : (r.currency || '') === self.selectedCurrency;
      });
    },
    topCustomersGrouped: function() {
      return this.groupRowsByCurrency(this.topCustomersFiltered, 'currency');
    },
    topProductsGrouped: function() {
      return this.groupRowsByCurrency(this.topProductsFiltered, 'currency');
    },
    perCurrencyTotals: function() {
      if (!this.isAllCurrencies) return [];
      var totals = {};
      this.filteredRegionRows.forEach(function(r) {
        var c = r.currency_code || '?';
        totals[c] = (totals[c] || 0) + Number(r.total_spend || 0);
      });
      return Object.keys(totals).sort().map(function(c) {
        return { currency: c, total: totals[c] };
      });
    },
    doughnutsByCurrency: function() {
      if (!this.isAllCurrencies) return [];
      var groups = {};
      this.filteredRegionRows.forEach(function(r) {
        var c = r.currency_code || '?';
        if (!groups[c]) groups[c] = { currency: c, labels: [], values: [], colors: [] };
        groups[c].labels.push(r.region_code);
        groups[c].values.push(Number(r.total_spend || 0));
        groups[c].colors.push(REGION_COLORS[r.region_code] || '#94a3b8');
      });
      return Object.keys(groups).sort().map(function(c) { return groups[c]; });
    },
    trendByCurrency: function() {
      var self = this;
      if (!this.isAllCurrencies) return [];
      var seen = {};
      (this.dashboard.monthlySpend || []).forEach(function(r) {
        if (!self.inSelectedRegion(r)) return;
        if (r.currency_code && Number(r.total_spend || 0) > 0) seen[r.currency_code] = true;
      });
      return Object.keys(seen).sort().map(function(c) {
        var buckets = self.buildMonthlyBuckets(c);
        return {
          currency: c,
          labels: buckets.map(function(b) { return self.formatMonth(b.key + '-01'); }),
          values: buckets.map(function(b) { return b.value; })
        };
      });
    },
    totalSeatsLabel: function() {
      var totals = this.dashboard.totals;
      if (!totals || totals.totalSeats === null || totals.totalSeats === undefined) return '--';
      return formatCount(Math.round(Number(totals.totalSeats)));
    },
    regionsCount: function() {
      if (!this.dashboard.totalSpendByRegion) return 0;
      var seen = {};
      for (var i = 0; i < this.dashboard.totalSpendByRegion.length; i++) {
        seen[this.dashboard.totalSpendByRegion[i].region_code] = true;
      }
      return Object.keys(seen).length;
    },
    totalSpend: function() {
      var total = 0;
      for (var i = 0; i < this.filteredRegionRows.length; i++) {
        total += Number(this.filteredRegionRows[i].total_spend || 0);
      }
      return total;
    },
    invoiceCount: function() {
      var count = 0;
      for (var i = 0; i < this.filteredRegionRows.length; i++) {
        count += Number(this.filteredRegionRows[i].invoice_count || 0);
      }
      return count;
    },
    topCustomerName: function() {
      if (this.isAllCurrencies) {
        var names = [];
        this.topCustomersGrouped.forEach(function(g) {
          if (g.rows.length > 0 && g.rows[0].customer_name) {
            names.push(g.rows[0].customer_name + ' (' + g.currency + ')');
          }
        });
        if (names.length === 0) return '--';
        var extra = names.length > 3 ? ' +' + (names.length - 3) : '';
        return names.slice(0, 3).join(', ') + extra;
      }
      if (this.topCustomersFiltered.length === 0) return '--';
      return this.topCustomersFiltered[0].customer_name || '--';
    },
    topProductName: function() {
      if (this.isAllCurrencies) {
        var names = [];
        this.topProductsGrouped.forEach(function(g) {
          if (g.rows.length > 0 && g.rows[0].product_name) {
            names.push(g.rows[0].product_name + ' (' + g.currency + ')');
          }
        });
        if (names.length === 0) return '--';
        var extra = names.length > 3 ? ' +' + (names.length - 3) : '';
        return names.slice(0, 3).join(', ') + extra;
      }
      if (this.topProductsFiltered.length === 0) return '--';
      return this.topProductsFiltered[0].product_name || '--';
    },
    syncStateRows: function() {
      var rows = (this.dashboard.syncState || []).filter(function(r) {
        return r.job_type === 'billing-full-sync';
      });
      return rows.length > 0 ? rows : (this.dashboard.syncState || []);
    },
    lastSyncLabel: function() {
      var rows = this.syncStateRows;
      if (rows.length === 0) return '--';
      var mostRecent = null;
      for (var i = 0; i < rows.length; i++) {
        var syncedAt = rows[i].last_synced_at;
        if (syncedAt && (!mostRecent || syncedAt > mostRecent)) {
          mostRecent = syncedAt;
        }
      }
      return mostRecent ? formatDate(mostRecent, { mode: 'relative' }) : '--';
    },
    monthlyBuckets: function() {
      if (this.isAllCurrencies) return [];
      return this.buildMonthlyBuckets(this.selectedCurrency);
    },
    monthlyChartLabels: function() {
      var self = this;
      return this.monthlyBuckets.map(function(b) { return self.formatMonth(b.key + '-01'); });
    },
    monthlyChartValues: function() {
      return this.monthlyBuckets.map(function(b) { return b.value; });
    },
    regionChartLabels: function() {
      return this.filteredRegionRows.map(function(r) { return r.region_code; });
    },
    regionChartValues: function() {
      return this.filteredRegionRows.map(function(r) { return Number(r.total_spend || 0); });
    },
    regionChartColors: function() {
      return this.filteredRegionRows.map(function(r) { return REGION_COLORS[r.region_code] || '#94a3b8'; });
    }
  },
  watch: {
    selectedRange: function() { this.syncQueryState(); },
    selectedCurrency: function() { this.syncQueryState(); },
    selectedRegion: function() { this.syncQueryState(); }
  },
  methods: {
    initFromQuery: function() {
      if (!this.$route) return;
      var q = this.$route.query || {};
      var range = parseInt(q.range, 10);
      for (var i = 0; i < this.timeRanges.length; i++) {
        if (this.timeRanges[i].value === range) {
          this.selectedRange = range;
          break;
        }
      }
      if (typeof q.currency === 'string' && q.currency) this.selectedCurrency = q.currency;
      if (typeof q.region === 'string' && q.region) this.selectedRegion = q.region;
    },
    syncQueryState: function() {
      if (!this.$router) return;
      var query = Object.assign({}, this.$route.query, {
        range: String(this.selectedRange),
        currency: this.selectedCurrency,
        region: this.selectedRegion
      });
      if (query.range === '12') delete query.range;
      if (query.currency === 'GBP') delete query.currency;
      if (query.region === 'ALL') delete query.region;
      this.$router.replace({ query: query }).catch(function() {});
    },
    formatCurrency: function(amount, currency) {
      return formatCurrency(amount, currency);
    },
    formatCompactCurrency: function(amount, currency) {
      return formatCompactCurrency(amount, currency);
    },
    getRegionColor: getRegionColor,
    formatCount: formatCount,
    formatProductQuantity: function(product) {
      var seats = Number(product.licensed_seats);
      if (product.licensed_seats !== null && product.licensed_seats !== undefined && !isNaN(seats)) {
        return formatCount(Math.round(seats)) + ' seats';
      }
      var units = Number(product.usage_units);
      if (product.usage_units !== null && product.usage_units !== undefined && !isNaN(units)) {
        return formatCount(Math.round(units)) + ' ' + this.usageUnitLabel(product.usage_unit_label);
      }
      return '--';
    },
    usageUnitLabel: function(label) {
      return { '1 Hour': 'hours', '1/Hour': 'hours', '1 GB/Month': 'GB-months', '1/Month': 'months' }[label] || label || 'units';
    },
    inSelectedRegion: function(row) {
      return this.selectedRegion === 'ALL' || row.region_code === this.selectedRegion;
    },
    groupRowsByCurrency: function(rows, currencyKey) {
      var order = [];
      var map = {};
      rows.forEach(function(r) {
        var c = r[currencyKey] || '?';
        if (!map[c]) {
          map[c] = { currency: c, rows: [] };
          order.push(c);
        }
        map[c].rows.push(r);
      });
      order.sort();
      return order.map(function(c) { return map[c]; });
    },
    buildMonthlyBuckets: function(currency) {
      var n = this.selectedRange;
      var now = new Date();
      var sums = {};
      var rows = this.dashboard.monthlySpend || [];
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if ((row.currency_code || '') !== currency) continue;
        if (!this.inSelectedRegion(row)) continue;
        var key = String(row.billing_month).slice(0, 7);
        sums[key] = (sums[key] || 0) + Number(row.total_spend || 0);
      }
      var buckets = [];
      for (var j = n; j >= 1; j--) {
        var d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - j, 1));
        var k = d.toISOString().slice(0, 7);
        buckets.push({ key: k, value: sums[k] || 0 });
      }
      return buckets;
    },
    selectRegion: function(region) {
      this.selectedRegion = region || 'ALL';
      this.loadData();
    },
    formatRelativeTime: function(isoString) {
      if (!isoString) return '--';
      return formatDate(isoString, { mode: 'relative' }) || formatDate(isoString, { mode: 'auto', includeTime: false });
    },
    formatMonth: function(monthStr) {
      if (!monthStr) return '--';
      var d = new Date(monthStr);
      if (isNaN(d.getTime())) return '--';
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[d.getUTCMonth()] + " '" + String(d.getUTCFullYear()).slice(2);
    },
    selectRange: function(range) {
      this.selectedRange = range;
      this.loadData();
    },
    loadData: function() {
      var self = this;
      var token = ++this.loadToken;
      this.error = '';
      this.dashboardState = 'loading';

      var params = { months: this.selectedRange };
      if (this.selectedRegion !== 'ALL') params.regionCode = this.selectedRegion;
      getBillingDashboard(params).then(function(data) {
        if (token !== self.loadToken) return;
        self.dashboard = data;
        self.dashboardState = 'loaded';
      }).catch(function(err) {
        if (token !== self.loadToken) return;
        console.error('Failed to load billing dashboard:', err);
        self.error = err.message || 'Failed to load billing data.';
        self.dashboardState = 'error';
      });
    },
    triggerSync: function() {
      var self = this;
      this.syncing = true;
      this.syncStatus = 'Triggering sync for all regions...';
      this.syncStatusType = 'info';

      triggerBillingSync({ jobType: 'billing-full-sync' }).then(function(data) {
        var jobs = data.jobs || [];
        if (jobs.length === 0) {
          self.syncStatus = 'No active regions to sync.';
          self.syncStatusType = 'warning';
          self.syncing = false;
          return;
        }
        self.syncStatus = 'Sync triggered for ' + jobs.length + ' region(s). Polling for completion...';
        self.syncStatusType = 'info';
        self.pollJobs(jobs.map(function(j) { return j.jobId; }));
      }).catch(function(err) {
        self.syncStatus = 'Sync failed: ' + (err.message || 'Unknown error');
        self.syncStatusType = 'error';
        self.syncing = false;
      });
    },
    pollJobs: function(jobIds) {
      var self = this;
      var completed = 0;
      var failed = 0;

      function checkAll() {
        var promises = jobIds.map(function(jobId) {
          return getBillingJobStatus(jobId);
        });

        Promise.all(promises).then(function(results) {
          completed = 0;
          failed = 0;
          for (var i = 0; i < results.length; i++) {
            if (results[i].status === 'succeeded') completed++;
            else if (results[i].status === 'failed') failed++;
          }

          if (completed + failed === jobIds.length) {
            self.syncing = false;
            if (failed > 0) {
              self.syncStatus = 'Sync completed: ' + completed + ' succeeded, ' + failed + ' failed.';
              self.syncStatusType = 'warning';
            } else {
              self.syncStatus = 'Sync completed successfully for all regions.';
              self.syncStatusType = 'success';
            }
            self.loadData();
          } else {
            self.syncStatus = 'Syncing: ' + completed + ' completed, ' + failed + ' failed, ' + (jobIds.length - completed - failed) + ' pending...';
            self.pollTimer = setTimeout(checkAll, 5000);
          }
        }).catch(function() {
          self.pollTimer = setTimeout(checkAll, 5000);
        });
      }

      checkAll();
    }
  },
  mounted: function() {
    var self = this;
    this.initFromQuery();
    getBillingRegions().then(function(data) {
      var items = (data && data.items) || [];
      self.regionOptions = items
        .filter(function(r) { return r.status === 'active'; })
        .map(function(r) { return r.region_code; })
        .sort();
    }).catch(function() {});
    this.loadData();
  },
  beforeUnmount: function() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }
};
</script>

<style scoped>
.billing-dashboard {
  min-height: 100%;
  --bd-ink: #0f172a;
  --bd-text: #334155;
  --bd-subtle: #475569;
  --bd-muted: #64748b;
  --bd-faint: #94a3b8;
  --bd-surface: #ffffff;
  --bd-sunken: #f8fafc;
  --bd-wash: #f1f5f9;
  --bd-border: #e2e8f0;
  --bd-accent: #4f46e5;
  --bd-accent-hover: #4338ca;
}

.page-body {
  padding: 24px;
}

/* ===== Dashboard Header ===== */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.dashboard-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sync-meta {
  font-size: 0.75rem;
  color: var(--bd-muted);
  font-weight: 500;
  white-space: nowrap;
}

.range-tabs {
  display: flex;
  gap: 4px;
  background: var(--bd-wash);
  border-radius: 10px;
  padding: 4px;
}
.range-tab {
  padding: 6px 14px;
  border: none;
  background: transparent;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--bd-muted);
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}
.range-tab:hover {
  color: var(--bd-text);
  background: var(--bd-border);
}
.range-tab.active {
  background: var(--bd-surface);
  color: var(--bd-accent);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.range-tab:focus-visible,
.currency-filter-select:focus-visible,
.sync-btn:focus-visible,
.retry-btn:focus-visible {
  outline: 2px solid var(--bd-accent);
  outline-offset: 2px;
}

.dashboard-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.currency-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}
.currency-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bd-muted);
}
.currency-filter-select {
  border: 1px solid var(--bd-border);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  font-family: 'Inter', sans-serif;
  color: var(--bd-text);
  background: var(--bd-surface);
}

/* ===== KPI Grid ===== */
.kpi-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.kpi-grid :deep(.metric-card) {
  padding-left: 16px;
}
.kpi-grid :deep(.metric-value) {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--bd-ink);
  letter-spacing: -0.02em;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi-grid :deep(.metric-label) {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bd-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Hero KPI */
.kpi-hero {
  background: var(--bd-surface);
  border: 1px solid var(--bd-border);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.kpi-hero .metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bd-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.kpi-hero .metric-value {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--bd-ink);
  line-height: 1.1;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}
.kpi-hero .metric-sub {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--bd-muted);
  margin-top: 4px;
}

/* ===== Sync Button ===== */
.sync-btn {
  background: var(--bd-accent);
  color: var(--bd-surface);
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(79, 70, 229, 0.2);
  font-family: 'Inter', sans-serif;
}
.sync-btn:hover:not(:disabled) {
  background: var(--bd-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}
.sync-btn:active:not(:disabled) {
  transform: translateY(0);
}
.sync-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.sync-btn svg { transition: transform 0.6s ease; }
.sync-btn.syncing svg { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.sync-status {
  font-size: 0.8125rem;
  font-weight: 500;
}
.sync-status-info { color: #2563eb; }
.sync-status-success { color: #16a34a; }
.sync-status-warning { color: #ca8a04; }
.sync-status-error { color: #dc2626; }

/* ===== Dashboard Grid ===== */
.dashboard-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  align-items: start;
}

.card {
  background: var(--bd-surface);
  border: 1px solid var(--bd-border);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
  transition: box-shadow 0.2s ease;
}
.card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

.card h3 {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--bd-ink);
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
}

.chart-card-wide {
  grid-column: 1 / -1;
}

.tables-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-column: 1;
  grid-row: 2;
}

.dashboard-grid > .card:nth-child(2) {
  grid-column: 2;
  grid-row: 2;
}

/* ===== Charts ===== */
.chart-wrapper {
  height: 280px;
  position: relative;
}

.hero-currency-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 8px;
}
.hero-currency-block {
  min-width: 130px;
}
.hero-currency-code {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--bd-faint);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.trend-multi {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}
.chart-wrapper-mini {
  height: 220px;
  position: relative;
}
.mini-chart-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--bd-text);
  margin: 0 0 8px;
}

.doughnut-multi {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}
.doughnut-mini {
  min-width: 220px;
}

.currency-group {
  margin-bottom: 16px;
}
.currency-group:last-child {
  margin-bottom: 0;
}
.currency-group-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--bd-muted);
  margin: 0 0 4px;
  letter-spacing: 0.04em;
}
.chart-wrapper-doughnut {
  height: 260px;
  position: relative;
  width: 100%;
  max-width: 260px;
  margin: 0 auto;
}

/* ===== No Data ===== */
.no-data {
  font-size: 0.875rem;
  color: var(--bd-faint);
  text-align: center;
  padding: 32px 16px;
  background: var(--bd-sunken);
  border-radius: 8px;
  border: 1px dashed var(--bd-border);
  margin-top: 8px;
}

/* ===== Tables ===== */
.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8125rem;
}

.data-table th {
  text-align: left;
  padding: 10px 12px;
  background: var(--bd-wash);
  color: var(--bd-subtle);
  font-weight: 700;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--bd-border);
}

.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--bd-wash);
  color: var(--bd-text);
  vertical-align: middle;
}
.data-table th:first-child,
.data-table td:first-child {
  border-left: 3px solid transparent;
  border-left-color: inherit;
}
.data-table tbody tr {
  transition: background 0.15s;
}
.data-table tbody tr:nth-child(even) {
  background: var(--bd-sunken);
}
.data-table tbody tr:hover {
  background: var(--bd-sunken);
}
.data-table tbody tr:last-child td {
  border-bottom: none;
}

.currency-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--bd-ink);
}

/* ===== Badges & Chips ===== */
.region-badge {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid #dbeafe;
  display: inline-block;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.status-chip::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
}
.status-succeeded { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
.status-failed    { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.status-running   { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.status-queued    { background: var(--bd-sunken); color: var(--bd-subtle); border-color: var(--bd-border); }

.sync-state-card {
  margin-bottom: 16px;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.retry-btn {
  background: var(--bd-surface);
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}
.retry-btn:hover {
  background: #fee2e2;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}
.skeleton {
  background: var(--bd-border);
  border-radius: 8px;
  animation: pulse 1.5s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.skeleton-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
}
.skeleton-card  { height: 120px; width: 100%; }
.skeleton-chart { height: 320px; width: 100%; }

@media (max-width: 1024px) {
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
  }
  .skeleton-row {
    grid-template-columns: 1fr 1fr;
  }
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .tables-column,
  .dashboard-grid > .card:nth-child(2) {
    grid-column: auto;
    grid-row: auto;
  }
}

@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  .skeleton-row {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sync-btn.syncing svg,
  .skeleton {
    animation: none;
  }
  .card,
  .range-tab,
  .sync-btn,
  .retry-btn,
  .data-table tbody tr {
    transition: none;
  }
}
</style>
